import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Paper, Tabs, Tab, TextField, Button, InputAdornment, IconButton, MenuItem, Alert } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import DisclaimerB4LogIn from './DisclaimerB4LogIn';
import { isMarketResolved } from '../utils/disclaimerForMarket';
import ReCAPTCHA from 'react-google-recaptcha';

function FeedbackField({ message }) {
  return (
    <TextField
      value={message}
      InputProps={{ readOnly: true }}
      variant="filled"
      fullWidth
      margin="dense"
      sx={{ mt: 2, mb: 0, background: '#f5f5f5' }}
    />
  );
}

FeedbackField.propTypes = {
  message: PropTypes.string.isRequired,
};

const Login = () => {
    const { login, logout, api, user } = useAuth();
    const { config } = useConfig();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isExpiredRedirect = searchParams.get('expired') === 'true';

    const [tab, setTab] = useState(() => {
        const query = new URLSearchParams(window.location.search);
        if (query.get('expired') === 'true') return 0; // show login tab for expired users
        return query.has('session_id') ? 1 : 0;
    });

    const [loginId, setLoginId] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [signup, setSignup] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '', countryCode: 'US' });
    const [trial, setTrial] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
    const [showTrialPassword, setShowTrialPassword] = useState(false);
    const [showTrialConfirmPassword, setShowTrialConfirmPassword] = useState(false);
    const [showSignupPassword, setShowSignupPassword] = useState(false);
    const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [priceIds, setPriceIds] = useState([]);
    const [selectedPrice, setSelectedPrice] = useState('');
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const stripePromise = window.Stripe('pk_test_51SQFWZLxxKRaIt4mjIDEsAwiGIqArNA4xVGyh9SELyoAUnDaXJ7i1Re0rpEvOmOuyHo0fhPyXabRdSftR3SSTz8F003uEhRjBV');
    const recaptchaRef = useRef();

    useEffect(() => {
        const completePayment = async (sessionId) => {
            try {
                setFeedback('Verifying payment...');
                const response = await api.post('/api/stripe/complete-session', { sessionId });
                const { token } = response.data;
                if (token) {
                    setFeedback('Payment successful! Logging you in...');
                    await login(null, null, token);
                    navigate('/');
                } else {
                    throw new Error('Failed to retrieve login token after payment.');
                }
            } catch (error) {
                setFeedback(error.message);
            }
        };

        const query = new URLSearchParams(window.location.search);
        const sessionId = query.get('session_id');
        if (query.get('payment') === 'success' && sessionId) {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            completePayment(sessionId);
        } else if (query.get('payment') === 'cancelled') {
            setFeedback('Payment was cancelled. You can try again anytime.');
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }
    }, [login, api, navigate]);

    const fetchPricingPlans = async () => {
        try {
            const response = await api.get('/api/pricing-plans');
            const plans = response.data;
            const planIds = plans.map(p => ({ id: p.planId, name: `${p.name} - $${p.price}` }));
            setPriceIds(planIds);
            if (planIds.length > 0) {
                setSelectedPrice(planIds[0].id);
            }
        } catch (error) {
            console.error('Failed to fetch pricing plans:', error);
            setFeedback('Could not load subscription plans. Please refresh.');
        }
    };

    const handleLogin = async () => {
        if (!loginId || !loginPassword) {
            setFeedback('Please enter both Email and Password.');
            return;
        }
        try {
            setFeedback('Logging in...');
            const success = await login(loginId, loginPassword);
            if (success) {
                setFeedback('Login successful. Please review the disclaimer.');
                setShowDisclaimer(true);
            }
        } catch (error) {
            console.error('Login error:', error);
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                setFeedback('Invalid email or password.');
            } else if (error.code === 'auth/too-many-requests') {
                setFeedback('Too many failed attempts. Please try again later.');
            } else {
                setFeedback('Login failed. Please try again.');
            }
        }
    };

    const handleAcceptDisclaimer = () => {
        setShowDisclaimer(false);
        navigate('/');
    };

    const handleExitDisclaimer = () => {
        setShowDisclaimer(false);
        logout();
    };

    const handleTrialSignup = async (e) => {
        e.preventDefault();
        if (trial.password !== trial.confirmPassword) {
            setFeedback('Passwords do not match.');
            return;
        }
        if (!trial.email || !trial.password || !trial.firstName) {
            setFeedback('Please fill in all required fields.');
            return;
        }
        setFeedback('Creating trial account...');
        try {
            let recaptchaToken = '';
            try {
                recaptchaToken = await Promise.race([
                    recaptchaRef.current.executeAsync(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('reCAPTCHA timeout')), 5000)),
                ]);
                recaptchaRef.current.reset();
            } catch (rcErr) {
                console.warn('reCAPTCHA failed, proceeding without token:', rcErr.message);
                if (recaptchaRef.current) recaptchaRef.current.reset();
            }

            // Country follows the deployment, not a literal. This was hardcoded to 'US', so a
            // trial started on the India site announced itself as a US signup. The backend
            // stamps marketId from its own `marketdata.market` and ignores this field, which is
            // why the mismatch caused no visible error — but sending the wrong country while
            // the server quietly disagrees is exactly the kind of thing that misleads whoever
            // debugs the next problem.
            await api.post('/api/auth/trial-signup', {
                email: trial.email,
                password: trial.password,
                firstName: trial.firstName,
                lastName: trial.lastName || '',
                countryCode: config?.market === 'IN' ? 'IN' : 'US',
                marketId: config?.market === 'IN' ? 2 : 1,
                recaptchaToken,
            });

            setFeedback('Trial account created! Logging you in...');
            const success = await login(trial.email, trial.password);
            if (success) {
                setFeedback('Login successful. Please review the disclaimer.');
                setShowDisclaimer(true);
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Trial signup failed.';
            setFeedback(msg);
            console.error('Trial signup error:', error);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setFeedback('Processing...');
        try {
            // Same guard as the trial path above: the widget is not rendered when no site key
            // is configured, so the ref is null and calling executeAsync on it would surface
            // as a signup failure rather than a missing bot check.
            let recaptchaToken = '';
            if (recaptchaRef.current) {
                try {
                    recaptchaToken = await recaptchaRef.current.executeAsync();
                    recaptchaRef.current.reset();
                } catch (rcErr) {
                    console.warn('reCAPTCHA failed, proceeding without token:', rcErr.message);
                    if (recaptchaRef.current) recaptchaRef.current.reset();
                }
            }

            // Add countryCode, marketId, and recaptchaToken to the payload
            const marketId = signup.countryCode === 'IN' ? 2 : 1;
            const signupPayload = { ...signup, recaptchaToken, marketId };
            await api.post('/api/auth/signup', signupPayload);

            if (signup.countryCode === 'IN') {
                setFeedback('Account created! Please wait for manual activation.');
                return;
            }

            if (!selectedPrice) {
                throw new Error('Please select a subscription plan.');
            }

            const checkoutResponse = await api.post('/api/stripe/create-checkout-session-unauthenticated', { 
                planId: selectedPrice, 
                email: signup.email 
            });

            const session = checkoutResponse.data;
            const stripe = await stripePromise;
            const { error } = await stripe.redirectToCheckout({ sessionId: session.id });
            if (error) {
                throw new Error(`Redirect to checkout failed: ${error.message}`);
            }
        } catch (error) {
            setFeedback(error.message);
            console.error('Signup and Pay Error:', error);
        }
    };

    return (
        <>
            <DisclaimerB4LogIn
                open={showDisclaimer}
                onAccept={handleAcceptDisclaimer}
                onExit={handleExitDisclaimer}
                config={config}
                // From the customer's own profile. The dialog opens as soon as login resolves,
                // which is before the profile arrives, so it is told to wait rather than fall
                // back to the deployment default and risk showing the wrong market's terms.
                market={user?.marketId ?? user?.market}
                marketResolved={isMarketResolved(user)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f5f5' }}>
            <Paper elevation={3} sx={{ p: 2, width: '100%', maxWidth: '450px' }}>
                {/* Neutral by necessity: this is shown BEFORE authentication, so the customer's
                    market is unknown and any brand here would be a guess — the US name in front
                    of an India customer, or the reverse. Branding resumes once they are signed in
                    and their market is known. */}
                <Box sx={{ width: '100%', background: '#e3f2fd', color: '#1565c0', borderRadius: 1, py: 2, fontWeight: 'bold', textAlign: 'center', fontSize: 22, letterSpacing: 0.5, mb: 2 }}>
                    Log In
                </Box>
                {isExpiredRedirect && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Your trial has expired. Please subscribe to continue using AstroStockCharts.
                    </Alert>
                )}

                <Tabs value={tab} onChange={(_, v) => { 
                    // Prevent switching to disabled tabs
                    if ((config?.market === 'US' && v === 2) || 
                        (config?.market === 'IN' && v === 1)) {
                        return;
                    }
                    setTab(v); 
                    setFeedback(''); 
                    if (v === 1) { setSignup(s => ({ ...s, countryCode: 'US' })); fetchPricingPlans(); }
                    if (v === 2) { setSignup(s => ({ ...s, countryCode: 'IN' })); }
                }} centered sx={{ mb: 2 }}>
                    <Tab label="Log In" />
                    <Tab 
                        label={config?.market === 'IN' ? "Reserved" : "Sign Up (US)"} 
                        disabled={true}
                    />
                    <Tab 
                        label={config?.market === 'US' ? "Reserved" : "Sign Up (India)"} 
                        disabled={config?.market === 'US'} 
                    />
                    <Tab label="Free Trial" />
                </Tabs>

                {tab === 0 && (
                    <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }} display="flex" flexDirection="column" gap={2} p={2}>
                        <TextField label="Email" value={loginId} onChange={e => setLoginId(e.target.value)} fullWidth autoFocus margin="dense" />
                        <TextField
                            label="Password"
                            value={loginPassword}
                            onChange={e => setLoginPassword(e.target.value)}
                            type={showLoginPassword ? 'text' : 'password'}
                            fullWidth
                            margin="dense"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowLoginPassword(v => !v)} edge="end">
                                            {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button type="submit" variant="contained" sx={{ mt: 1 }}>Log In</Button>
                    </Box>
                )}

                {tab === 1 && ( // US Signup
                    <Box component="form" onSubmit={handleSignup} display="flex" flexDirection="column" gap={2} p={2}>
                        <TextField label="First Name" value={signup.firstName} onChange={e => setSignup(s => ({ ...s, firstName: e.target.value }))} fullWidth margin="dense" />
                        <TextField label="Last Name" value={signup.lastName} onChange={e => setSignup(s => ({ ...s, lastName: e.target.value }))} fullWidth margin="dense" />
                        <TextField label="Email" value={signup.email} onChange={e => setSignup(s => ({ ...s, email: e.target.value }))} type="email" fullWidth margin="dense" />
                        <TextField label="Phone Number" value={signup.phone} onChange={e => setSignup(s => ({ ...s, phone: e.target.value.replace(/[^0-9-]/g, '') }))} type="tel" fullWidth margin="dense" />
                        <TextField select label="Select Plan" value={selectedPrice} onChange={e => setSelectedPrice(e.target.value)} fullWidth margin="dense" disabled={priceIds.length === 0}>
                            {priceIds.map(plan => (
                                <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField label="Password" value={signup.password} onChange={e => setSignup(s => ({ ...s, password: e.target.value }))} type={showSignupPassword ? 'text' : 'password'} fullWidth margin="dense" InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowSignupPassword(v => !v)} edge="end">{showSignupPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}} />
                        <TextField label="Confirm Password" value={signup.confirmPassword} onChange={e => setSignup(s => ({ ...s, confirmPassword: e.target.value }))} type={showSignupConfirmPassword ? 'text' : 'password'} fullWidth margin="dense" InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowSignupConfirmPassword(v => !v)} edge="end">{showSignupConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}} />
                        <Button type="submit" variant="contained" sx={{ mt: 1 }} disabled={priceIds.length === 0}>Sign Up & Pay</Button>
                    </Box>
                )}

                {tab === 2 && ( // India Signup
                    <Box component="form" onSubmit={handleSignup} display="flex" flexDirection="column" gap={2} p={2}>
                        <TextField label="First Name" value={signup.firstName} onChange={e => setSignup(s => ({ ...s, firstName: e.target.value }))} fullWidth margin="dense" />
                        <TextField label="Last Name" value={signup.lastName} onChange={e => setSignup(s => ({ ...s, lastName: e.target.value }))} fullWidth margin="dense" />
                        <TextField label="Email" value={signup.email} onChange={e => setSignup(s => ({ ...s, email: e.target.value }))} type="email" fullWidth margin="dense" />
                        <TextField label="Phone Number" value={signup.phone} onChange={e => setSignup(s => ({ ...s, phone: e.target.value.replace(/[^0-9-]/g, '') }))} type="tel" fullWidth margin="dense" />
                        <TextField label="Password" value={signup.password} onChange={e => setSignup(s => ({ ...s, password: e.target.value }))} type={showSignupPassword ? 'text' : 'password'} fullWidth margin="dense" InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowSignupPassword(v => !v)} edge="end">{showSignupPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}} />
                        <TextField label="Confirm Password" value={signup.confirmPassword} onChange={e => setSignup(s => ({ ...s, confirmPassword: e.target.value }))} type={showSignupConfirmPassword ? 'text' : 'password'} fullWidth margin="dense" InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowSignupConfirmPassword(v => !v)} edge="end">{showSignupConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}} />
                        <Button type="submit" variant="contained" sx={{ mt: 1 }}>Sign Up</Button>
                    </Box>
                )}

                {tab === 3 && ( // Free Trial
                    <Box component="form" onSubmit={handleTrialSignup} display="flex" flexDirection="column" gap={2} p={2}>
                        <TextField label="First Name *" value={trial.firstName} onChange={e => setTrial(s => ({ ...s, firstName: e.target.value }))} fullWidth autoFocus margin="dense" />
                        <TextField label="Last Name" value={trial.lastName} onChange={e => setTrial(s => ({ ...s, lastName: e.target.value }))} fullWidth margin="dense" />
                        <TextField label="Email *" value={trial.email} onChange={e => setTrial(s => ({ ...s, email: e.target.value }))} type="email" fullWidth margin="dense" />
                        <TextField label="Password *" value={trial.password} onChange={e => setTrial(s => ({ ...s, password: e.target.value }))} type={showTrialPassword ? 'text' : 'password'} fullWidth margin="dense" InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowTrialPassword(v => !v)} edge="end">{showTrialPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}} />
                        <TextField label="Confirm Password *" value={trial.confirmPassword} onChange={e => setTrial(s => ({ ...s, confirmPassword: e.target.value }))} type={showTrialConfirmPassword ? 'text' : 'password'} fullWidth margin="dense" InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowTrialConfirmPassword(v => !v)} edge="end">{showTrialConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}} />
                        <Button type="submit" variant="contained" color="success" sx={{ mt: 1 }}>Start 14-Day Free Trial</Button>
                    </Box>
                )}

                {feedback && <FeedbackField message={feedback} />}

                <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #e0e0e0', mt: 3 }}>
                    <a href={config?.websiteUrl || "https://tungaresearch.com"} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1565c0' }}>
                        Go to website
                    </a>
                </Box>
                {/* Only rendered when a site key exists — see the note in AuthDialog.js. An
                    undefined sitekey makes react-google-recaptcha throw, which takes the whole
                    login screen down with it. The submit handler above already tolerates a
                    missing token: it races executeAsync against a 5s timeout and proceeds
                    without one on failure. */}
                {/* eslint-disable no-undef */}
                {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    />
                )}
                {/* eslint-enable no-undef */}
            </Paper>
        </Box>
        </>
    );
};

export default Login;
